import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { listServices } from "@/lib/services.functions";
import { getMyApplication, submitPartnerApplication } from "@/lib/partner.functions";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const servicesQuery = queryOptions({ queryKey: ["services"], queryFn: () => listServices() });
const applicationQuery = queryOptions({ queryKey: ["my-application"], queryFn: () => getMyApplication() });

export const Route = createFileRoute("/_authenticated/partner/apply")({
  head: () => ({
    meta: [
      { title: "Partner application — Shields" },
      { name: "description", content: "Apply to become a verified Shields partner." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ApplyPage,
});

function ApplyPage() {
  const { data: services } = useSuspenseQuery(servicesQuery);
  const { data: existing } = useSuspenseQuery(applicationQuery);
  const navigate = useNavigate();

  const [tradingName, setTradingName] = useState(existing?.trading_name ?? "");
  const [legalName, setLegalName] = useState(existing?.legal_name ?? "");
  const [companyNumber, setCompanyNumber] = useState(existing?.company_number ?? "");
  const [contactEmail, setContactEmail] = useState(existing?.contact_email ?? "");
  const [contactPhone, setContactPhone] = useState(existing?.contact_phone ?? "");
  const [selected, setSelected] = useState<string[]>(existing?.services ?? []);
  const [yearsExperience, setYearsExperience] = useState(existing?.years_experience?.toString() ?? "");
  const [operatingAreas, setOperatingAreas] = useState(existing?.operating_areas ?? "");
  const [insuranceProvider, setInsuranceProvider] = useState(existing?.insurance_provider ?? "");
  const [insurancePolicy, setInsurancePolicy] = useState(existing?.insurance_policy ?? "");
  const [insuranceExpires, setInsuranceExpires] = useState(existing?.insurance_expires ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locked = existing?.status === "approved" || existing?.status === "suspended" || existing?.status === "submitted" || existing?.status === "under_review";

  function toggleService(slug: string) {
    setSelected((cur) => (cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const result = await submitPartnerApplication({
      data: {
        tradingName,
        legalName: legalName || undefined,
        companyNumber: companyNumber || undefined,
        contactEmail,
        contactPhone: contactPhone || undefined,
        services: selected,
        yearsExperience: yearsExperience ? Number(yearsExperience) : undefined,
        operatingAreas: operatingAreas || undefined,
        insuranceProvider: insuranceProvider || undefined,
        insurancePolicy: insurancePolicy || undefined,
        insuranceExpires: insuranceExpires || undefined,
      },
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? "Could not submit your application");
      return;
    }
    toast.success("Application submitted for human review");
    navigate({ to: "/account" });
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="section-pad mx-auto flex h-16 max-w-3xl items-center">
          <Link to="/account" className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" aria-hidden /> My account
          </Link>
        </div>
      </header>

      <main className="section-pad mx-auto max-w-3xl py-10">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold sm:text-3xl">Partner application</h1>
          {existing && <StatusPill status={existing.status} label={`Status: ${existing.status.replaceAll("_", " ")}`} />}
        </div>

        {locked ? (
          <div className="card-surface mt-8 p-8 text-center">
            <p className="text-muted-foreground">
              {existing?.status === "approved"
                ? "Your application is approved — your partner workspace is ready."
                : existing?.status === "suspended"
                  ? "Your account is suspended. Contact support from the Help page."
                  : "Your application is waiting for human review. You cannot use Partner operations until a Shields owner approves it."}
            </p>
            {existing?.status === "approved" && (
              <Link to="/partner" className="btn-hero mt-6 inline-flex">Open partner workspace</Link>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="card-surface mt-8 space-y-5 p-6 sm:p-8">
            {existing?.status === "more_info_required" && existing.reviewer_notes && (
              <p role="status" className="rounded-lg bg-warning/20 px-4 py-3 text-sm font-semibold text-warning-foreground">
                Our team asked for more information: {existing.reviewer_notes}
              </p>
            )}
            {error && (
              <p role="alert" className="rounded-lg bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{error}</p>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="tradingName">Trading name</Label>
                <Input id="tradingName" value={tradingName} onChange={(e) => setTradingName(e.target.value)} required maxLength={120} className="mt-1.5 min-h-12" />
              </div>
              <div>
                <Label htmlFor="legalName">Legal name (optional)</Label>
                <Input id="legalName" value={legalName} onChange={(e) => setLegalName(e.target.value)} maxLength={120} className="mt-1.5 min-h-12" />
              </div>
              <div>
                <Label htmlFor="companyNumber">Company number (optional)</Label>
                <Input id="companyNumber" value={companyNumber} onChange={(e) => setCompanyNumber(e.target.value)} maxLength={20} className="mt-1.5 min-h-12" />
              </div>
              <div>
                <Label htmlFor="yearsExperience">Years of experience</Label>
                <Input id="yearsExperience" type="number" min="0" max="80" value={yearsExperience} onChange={(e) => setYearsExperience(e.target.value)} className="mt-1.5 min-h-12" />
              </div>
              <div>
                <Label htmlFor="contactEmail">Contact email</Label>
                <Input id="contactEmail" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required className="mt-1.5 min-h-12" />
              </div>
              <div>
                <Label htmlFor="contactPhone">Contact phone (optional)</Label>
                <Input id="contactPhone" type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} maxLength={30} className="mt-1.5 min-h-12" />
              </div>
            </div>

            <fieldset>
              <legend className="text-sm font-medium">Services you offer</legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {services.map((s) => (
                  <label key={s.id} className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border px-4 text-sm font-semibold ${selected.includes(s.slug) ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input
                      type="checkbox"
                      checked={selected.includes(s.slug)}
                      onChange={() => toggleService(s.slug)}
                      className="h-4 w-4 accent-[var(--color-primary)]"
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <Label htmlFor="operatingAreas">Where do you work? (optional)</Label>
              <Textarea id="operatingAreas" value={operatingAreas} onChange={(e) => setOperatingAreas(e.target.value)} rows={2} maxLength={500} placeholder="e.g. Greater Manchester, within 15 miles of M1" className="mt-1.5" />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="insuranceProvider">Insurance provider</Label>
                <Input id="insuranceProvider" value={insuranceProvider} onChange={(e) => setInsuranceProvider(e.target.value)} maxLength={120} className="mt-1.5 min-h-12" />
              </div>
              <div>
                <Label htmlFor="insurancePolicy">Policy number</Label>
                <Input id="insurancePolicy" value={insurancePolicy} onChange={(e) => setInsurancePolicy(e.target.value)} maxLength={120} className="mt-1.5 min-h-12" />
              </div>
              <div>
                <Label htmlFor="insuranceExpires">Insurance expires</Label>
                <Input id="insuranceExpires" type="date" value={insuranceExpires} onChange={(e) => setInsuranceExpires(e.target.value)} className="mt-1.5 min-h-12" />
              </div>
            </div>

            <Button type="submit" disabled={busy || selected.length === 0} className="min-h-12 w-full font-bold">
              {busy ? "Submitting…" : existing ? "Resubmit application" : "Submit for review"}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Every application is reviewed by a person. Approval is never automatic.
            </p>
          </form>
        )}
      </main>
    </div>
  );
}
